import { Tag } from "@patientmpower/spiro";
import { useEffect, useMemo } from "react";

import { SelectMeasurementPayloadType } from "../../../../../../@types/Measurements";
import { ModalChart } from "../../../../../../components/ModalChart";
import {
  useArtiQSpirometryData,
  useSpirometryTimeVolume,
  useSpirometryVolumeFlow,
} from "../../../../../../hooks/queries/spirometry";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  HeaderContainer,
  Title,
  Subtitle,
} from "../../MeasurementModal.styles";
import { BlowAcceptabilitySection } from "../BlowAcceptabilitySection";
import { SpirometryValuesSection } from "../SpirometryValuesSection";
import {
  ArtiQContainer,
  ChartContainer,
  ContentContainer,
  SpirometryContainer,
  SubtitleGradeContainer,
} from "./SpirometrySection.styles";

type SpirometrySectionProps = {
  selectedMeasurement: SelectMeasurementPayloadType;
};

type TagVariants = "error" | "success" | "warning";

type GradeTagColor = {
  fvc: TagVariants;
  fev1: TagVariants;
};

export function SpirometrySection({
  selectedMeasurement,
}: SpirometrySectionProps) {
  const { widthView } = useIsMobile();

  const { artiQData } = useArtiQSpirometryData(
    selectedMeasurement?.spirometryTestIds || []
  );

  const fvcGrade = artiQData?.fvcGrade;
  const fev1Grade = artiQData?.fev1Grade;

  const spirometryGradesTagColor = useMemo<GradeTagColor>(() => {
    const fvcGrade = artiQData?.fvcGrade;
    const fev1Grade = artiQData?.fev1Grade;

    const grades: GradeTagColor = {
      fvc: "error",
      fev1: "error",
    };

    if (fvcGrade === "A") {
      grades.fvc = "success";
    }

    if (fev1Grade === "A") {
      grades.fev1 = "success";
    }

    if (fvcGrade === "B" || fvcGrade === "C") {
      grades.fvc = "warning";
    }

    if (fev1Grade === "B" || fev1Grade === "C") {
      grades.fev1 = "warning";
    }

    return grades;
  }, [artiQData?.fev1Grade, artiQData?.fvcGrade]);

  useEffect(() => {
    mixpanelActions.track("UserAction: Clicked Spirometry Datapoint");
  }, [selectedMeasurement]);

  const { volumeFlowValues } = useSpirometryVolumeFlow(
    selectedMeasurement?.spirometryTestIds
  );

  const { timeVolumeValues } = useSpirometryTimeVolume(
    selectedMeasurement?.spirometryTestIds
  );

  const measurementChartWidth = useMemo(() => {
    return widthView > 1530 ? 252 : 252;
  }, [widthView]);

  const yLabelConfig = (value: string) => ({
    value,
    angle: -90,
    fontSize: "12px",
    position: "insideLeft",
  });

  const xLabelConfig = (value: string) => ({
    value,
    fontSize: "12px",
    position: "insideBottomLeft",
  });

  return (
    <SpirometryContainer>
      <HeaderContainer css={{ marginLeft: 0, marginBottom: 38 }}>
        <Title>Selected session</Title>

        <SubtitleGradeContainer>
          <Subtitle css={{ marginTop: 0, marginBottom: 0 }}>
            {selectedMeasurement.time}
          </Subtitle>

          {fvcGrade && (
            <Tag variant={spirometryGradesTagColor.fvc}>
              FVC = {artiQData?.fvcGrade}
            </Tag>
          )}

          {fev1Grade && (
            <Tag variant={spirometryGradesTagColor.fev1}>
              FEV1 = {artiQData?.fev1Grade}
            </Tag>
          )}
        </SubtitleGradeContainer>
      </HeaderContainer>

      <ContentContainer>
        <ChartContainer css={{ width: measurementChartWidth }}>
          <ModalChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              label: xLabelConfig("Vol (L)"),
            }}
            yConfig={{
              axisSize: 42,
              label: yLabelConfig("Flow (L/s)"),
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            disableTooltip
            data={volumeFlowValues || []}
          />

          <ModalChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              label: xLabelConfig("Time (s)"),
            }}
            yConfig={{
              axisSize: 42,
              label: yLabelConfig("Vol (L)"),
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            disableTooltip
            data={timeVolumeValues}
          />
        </ChartContainer>

        <ArtiQContainer>
          <SpirometryValuesSection bestValues={artiQData?.bestValues} />

          <BlowAcceptabilitySection
            fvcAcceptability={artiQData?.fvcAcceptability}
            fev1Acceptability={artiQData?.fev1Acceptability}
            fvcAcceptabilityReason={artiQData?.fvcAcceptabilityReason}
          />
        </ArtiQContainer>
      </ContentContainer>
    </SpirometryContainer>
  );
}
